/** Display form for a per-church ticket number: 1 → 0001. */
export const formatTicketNumber = (number: number): string => String(number).padStart(4, '0');

export const SUPPORT_TICKET_CATEGORIES = [
  'billing',
  'giving',
  'access',
  'bug',
  'how_to',
  'other',
] as const;

export type SupportTicketCategory = (typeof SUPPORT_TICKET_CATEGORIES)[number];

export const CHURCH_SUPPORT_PRIORITIES = ['low', 'normal', 'high'] as const;
export type ChurchSupportPriority = (typeof CHURCH_SUPPORT_PRIORITIES)[number];

export const SUPPORT_TICKET_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type SupportTicketPriority = (typeof SUPPORT_TICKET_PRIORITIES)[number];

export const SUPPORT_TICKET_STATUSES = [
  'open',
  'awaiting_church',
  'awaiting_platform',
  'escalated',
  'resolved',
  'closed',
] as const;

export type SupportTicketStatus = (typeof SUPPORT_TICKET_STATUSES)[number];

export type SupportTicketSide = 'church' | 'platform';

export type SupportTicketEventKind =
  | 'created'
  | 'message'
  | 'assigned'
  | 'unassigned'
  | 'priority_changed'
  | 'status_changed'
  | 'escalated'
  | 'visit_linked'
  | 'visit_started'
  | 'resolved'
  | 'reopened'
  | 'closed';

export interface SupportTicketAttachment {
  id: string;
  fileName: string;
  contentType: string;
  byteSize: number;
  kind: 'image' | 'document';
  downloadUrl: string | null;
}

export interface SupportTicketSummary {
  id: string;
  churchId: string;
  number: number;
  subject: string;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
  status: SupportTicketStatus;
  createdByName: string | null;
  assignedPlatformName: string | null;
  escalationLevel: number;
  slaFirstResponseDueAt: string;
  slaResolveDueAt: string;
  firstPlatformReplyAt: string | null;
  lastMessageAt: string;
  lastMessageSide: SupportTicketSide;
  createdAt: string;
  updatedAt: string;
}

export interface SupportTicketMessage {
  id: string;
  authorName: string | null;
  authorAvatarUrl: string | null;
  authorSide: SupportTicketSide;
  body: string;
  isInternal: boolean;
  attachments: SupportTicketAttachment[];
  createdAt: string;
}

export interface SupportTicketEvent {
  id: string;
  actorName: string | null;
  actorAvatarUrl: string | null;
  actorSide: 'church' | 'platform' | 'system';
  kind: SupportTicketEventKind;
  beforeValue: Record<string, unknown> | null;
  afterValue: Record<string, unknown> | null;
  messageId: string | null;
  createdAt: string;
}

export interface SupportTicketTimeline {
  ticket: SupportTicketSummary;
  messages: SupportTicketMessage[];
  events: SupportTicketEvent[];
}

export interface SupportAttachmentInput {
  fileKey: string;
  fileName: string;
  contentType: string;
  byteSize: number;
}
